/* The classification taxonomy: what an order type *is*, and where each queue
 * lives. This is the cockpit's own vocabulary, read by the case table, the case
 * rail, the routing tab, Pixie and the HTTP mapper alike.
 *
 * There is no prose here. An order type carries its TMF622 reading (`action`,
 * `category`, `productRef`), the queue it routes to and the weight it is drawn
 * with; a queue carries the path it lives at; a case state carries its weight
 * and a queue's desk carries which desk. What any of that is *called* is a
 * catalogue key, resolved by `vocabulary.ts` at render — the record key is the
 * catalogue key, so `orderTypes.MOVE` reads `domain:orderTypes.MOVE` and there
 * is no second table to keep in step.
 *
 * That is also what makes `map/cases.ts` able to invert this table: matching a
 * wire payload against `action` + `category` + `productRef` is a structural
 * question, and it stays answerable in any language.
 */

import type {
  CaseStateKey,
  DeskKey,
  OrderType,
  OrderTypeKey,
  QueueDefinition,
  QueueKey,
} from '@/shared/domain/case';
// `caseStates` carries each state's semantic disposition, drawn from the
// domain's own status vocabulary — not the presentation layer. Whether a
// disposition is drawn loud or quiet is `shared/ui/tone.ts`'s question, not
// this file's; this file, and everything that reads it, compares dispositions.
import type { StatusDisposition } from '@/shared/domain/status';

export const orderTypes: Readonly<Record<OrderTypeKey, OrderType>> = {
  NEW: {
    action: 'add',
    category: 'Provide',
    productRef: false,
    queue: 'work',
    disposition: 'ready',
  },
  MOVE: {
    action: 'modify',
    category: 'Move',
    productRef: true,
    queue: 'macd',
    disposition: 'automation',
  },
  ADD: {
    action: 'add',
    category: 'Add',
    productRef: true,
    queue: 'macd',
    disposition: 'automation',
  },
  CHANGE: {
    action: 'modify',
    category: 'Change',
    productRef: true,
    queue: 'macd',
    disposition: 'automation',
  },
  DISCONNECT: {
    action: 'delete',
    category: 'Cease',
    productRef: true,
    queue: 'macd',
    disposition: 'manual',
  },
  NONORDER: {
    action: '—',
    category: '—',
    productRef: false,
    queue: 'non-order',
    disposition: 'neutral',
    noOrder: true,
  },
  UNCLASSIFIED: {
    action: 'noChange',
    category: '—',
    productRef: false,
    queue: 'parked',
    disposition: 'blocked',
  },
};

export const queues: Readonly<Record<QueueKey, QueueDefinition>> = {
  work: { path: '/queue' },
  macd: { path: '/routed/macd' },
  catalog: { path: '/routed/catalog' },
  'non-order': { path: '/routed/non-order' },
  parked: { path: '/routed/parked' },
};

/** How much each lifecycle state should stand out, and nothing else.
 *
 * What a state is *called* is `domain:caseStates.<Key>.label`, and the step it
 * asks a person to take is `.action` — sixteen labels and sixteen instructions
 * that used to sit in `map/cases.ts` and were therefore baked into every row at
 * fetch time. The labels are not mechanical de-camel-casing: `ReadyForReview`
 * reads as "MRI complete" because that is what a reviewer is being told, and a
 * generated one would have said something else.
 *
 * `map/cases.ts` also reads this as the set of states it recognises — a wire
 * state that is not a key here has no name, and the row says so by showing the
 * token instead. */
export const caseStates: Readonly<Record<CaseStateKey, { readonly disposition: StatusDisposition }>> = {
  Received: { disposition: 'neutral' },
  CaseOpen: { disposition: 'neutral' },
  Analysing: { disposition: 'automation' },
  ClarificationNeeded: { disposition: 'clarification' },
  AwaitingApproval: { disposition: 'clarification' },
  AwaitingReply: { disposition: 'clarification' },
  ConflictDetected: { disposition: 'conflict' },
  ManualReview: { disposition: 'manual' },
  ReadyForReview: { disposition: 'ready' },
  SignedOff: { disposition: 'completed' },
  ManuallyTakenOver: { disposition: 'manual' },
  Fallback: { disposition: 'blocked' },
  Parked: { disposition: 'blocked' },
  RoutedOut: { disposition: 'neutral' },
  Closed: { disposition: 'neutral' },
  Purged: { disposition: 'neutral' },
};

/** Which desk owns each queue.
 *
 * Not a total answer on its own: a case awaiting a reply is with the requester
 * wherever it was routed, which `map/cases.ts` decides from the state before it
 * falls back to this. Two queues share the Order Manager, which is why this is a
 * table rather than a field on `queues`. */
export const desks: Readonly<Record<QueueKey, DeskKey>> = {
  work: 'orderManager',
  macd: 'macd',
  catalog: 'catalogAdmin',
  'non-order': 'routed',
  parked: 'orderManager',
};

export const queueKeys = Object.keys(queues) as readonly QueueKey[];

export const caseStateKeys = Object.keys(caseStates) as readonly CaseStateKey[];

export const orderTypeKeys = Object.keys(orderTypes) as readonly OrderTypeKey[];

/* Every answer the waiting-on column can give. `desks` above cannot serve as the
   list, because `requester` is not somewhere a queue routes to — it is the other
   party, and no queue maps to it. Written as a keyed object rather than an array
   so a `DeskKey` added to the union and forgotten here is a compile error rather
   than a silently unchecked catalogue entry. */
const allDesks: Readonly<Record<DeskKey, true>> = {
  requester: true,
  orderManager: true,
  macd: true,
  catalogAdmin: true,
  routed: true,
};

export const deskKeys = Object.keys(allDesks) as readonly DeskKey[];
